import { useState } from "react";
import { useParams } from "react-router-dom";
import { useUser } from "../api/hooks.js";
import { Card, ErrorState, Skeleton, StatePanel } from "../components/primitives.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import { P } from "../../shared/permissions.js";
import { Widget, WidgetSkeleton } from "../components/Widget.jsx";
import { useFilters } from "../lib/useFilters.js";
import { DataStrip, PageHeader } from "../components/PageHeader.jsx";
import { fmtDate } from "../lib/format.js";

function Field({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold tracking-[0.06em] text-ink-3 uppercase">{label}</p>
      <p className="truncate text-[13px] text-ink">{value}</p>
    </div>
  );
}

/**
 * One user's profile and activity, at /user/:uid. Internal only.
 * Shows this user's own data whatever tenant/profile filters are set; only the
 * date range applies (the API opens the request up).
 */
export default function UserPage() {
  const { uid } = useParams();
  const f = useFilters();
  const [reveal, setReveal] = useState(false);
  const { can } = useAuth();
  const qs = new URLSearchParams({ tenant: f.filters.tenant, from: f.filters.from, to: f.filters.to, ...(reveal ? { reveal: "1" } : {}) }).toString();
  const { data, error, isLoading, isFetching, refetch } = useUser(uid, qs);

  if (error?.kind === "forbidden") return <StatePanel title="No access" detail={error.message || "You do not have permission to view this user."} />;

  const u = data?.user;
  const basis = data ? `${fmtDate(data.window.prevFrom)} – ${fmtDate(data.window.prevTo)}` : "the previous period";
  const unknown = (v) => (v && v !== "Unknown" ? v : "");

  return (
    <div className="flex flex-col gap-4">
      <DataStrip data={data && { ...data, tenant: { name: u?.tenantName } }} isFetching={isFetching} today={f.today} />
      {/* A masked phone number is not a name; fall back to the id. */}
      <PageHeader title={u?.name && !u.name.includes("•") ? u.name : isLoading ? "Loading…" : `User ${uid}`} subtitle={`User ID ${uid}${u?.tenantName ? ` · ${u.tenantName}` : ""}`} />

      {error && <ErrorState what="this user" message={error.message} retryable={error.retryable !== false} onRetry={() => refetch()} />}

      {isLoading && <Skeleton height={96} />}
      {u && (
        <Card>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4 xl:grid-cols-6">
            <Field label="Profession" value={unknown(u.profession)} />
            <Field label="Speciality" value={unknown(u.specialty)} />
            <Field label="City" value={unknown(u.city)} />
            <Field label="State" value={unknown(u.state)} />
            <Field label="Country" value={unknown(u.country)} />
            <Field label="Age" value={u.age > 0 && u.age < 100 ? u.age : ""} />
            <Field label="Gender" value={u.gender} />
            <Field label="Registered" value={u.registered ? fmtDate(u.registered) : ""} />
            <Field label="Registration source" value={u.source} />
            <Field label="Email" value={u.email} />
            <Field label="Phone" value={u.phone} />
          </div>
          {u.phone && can(P.pii) && (
            <label className="mt-3 flex w-fit cursor-pointer items-center gap-1.5 text-[11px] text-ink-2">
              <input type="checkbox" checked={reveal} onChange={(e) => setReveal(e.target.checked)} />
              Show full phone number
            </label>
          )}
        </Card>
      )}

      <div className={`grid grid-cols-12 gap-4 transition-opacity ${isFetching && data ? "opacity-60" : ""}`}>
        {isLoading && [12, 8, 4, 6, 6].map((s, i) => <WidgetSkeleton key={i} span={s} />)}
        {data?.widgets.map((w) => (
          <Widget key={`${data.report}-${w.id}`} widget={w} page={data} basis={basis} filenameBase={`user_${uid}_${f.filters.from}_${f.filters.to}`} onRetry={refetch} />
        ))}
      </div>
    </div>
  );
}
